import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

export interface GoldenDocument {
  id: string;
  title: string;
  source_type: 'contentful' | 'manual' | 'additional_ai_content';
  source_reference: string | null;
  content_preview: string | null;
  content: string | null;
  priority: number;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_by: string | null;
  domain_id: string;
  domain_name: string;
  program_slug: string | null;
  unit_slug: string | null;
  unit_content_type: string | null;
  contentful_entry_id: string | null;
  content_fetched_at: string | null;
  is_stale: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContentfulProgram {
  program_name: string;
  program_slug: string;
  program_type: string;
}

export interface ContentfulProgramUnit {
  title: string;
  slug: string;
  level: number;
  content_type: string;
}

export interface ContentfulAdditionalAiContentEntry {
  id: string;
  title: string;
}

export interface GoldenDocCreateData {
  title: string;
  source_type: GoldenDocument['source_type'];
  source_reference?: string;
  content_preview?: string;
  content?: string;
  priority?: number;
  is_active?: boolean;
  domain_id: string;
  program_slug?: string;
  unit_slug?: string;
  unit_content_type?: string;
  contentful_entry_id?: string;
}

export const goldenDocService = {
  async list(params?: { domain_id?: string; source_type?: string; active?: string }): Promise<ApiResponse<JsonApiResource<GoldenDocument>[]>> {
    const queryParams: Record<string, string> = {};
    if (params?.domain_id) queryParams.domain_id = params.domain_id;
    if (params?.source_type) queryParams.source_type = params.source_type;
    if (params?.active) queryParams.active = params.active;
    return apiClient.get('/api/admin/golden_documents', queryParams);
  },

  async get(id: string): Promise<ApiResponse<JsonApiResource<GoldenDocument>>> {
    return apiClient.get(`/api/admin/golden_documents/${id}`);
  },

  async create(data: GoldenDocCreateData): Promise<ApiResponse<JsonApiResource<GoldenDocument>>> {
    return apiClient.post('/api/admin/golden_documents', { golden_document: data });
  },

  async update(id: string, data: Partial<GoldenDocCreateData>): Promise<ApiResponse<JsonApiResource<GoldenDocument>>> {
    return apiClient.patch(`/api/admin/golden_documents/${id}`, { golden_document: data });
  },

  async destroy(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/golden_documents/${id}`);
  },

  async reorder(items: { id: string; priority: number }[]): Promise<void> {
    return apiClient.patch('/api/admin/golden_documents/reorder', { items });
  },

  async listPrograms(): Promise<{ data: ContentfulProgram[] }> {
    return apiClient.get('/api/admin/golden_documents/programs');
  },

  async listProgramUnits(programSlug: string): Promise<{ data: ContentfulProgramUnit[] }> {
    return apiClient.get('/api/admin/golden_documents/program_units', { program_slug: programSlug });
  },

  async listAdditionalAiContent(): Promise<{ data: ContentfulAdditionalAiContentEntry[] }> {
    return apiClient.get('/api/admin/golden_documents/additional_ai_content');
  },

  async refreshContent(id: string): Promise<ApiResponse<JsonApiResource<GoldenDocument>>> {
    return apiClient.post(`/api/admin/golden_documents/${id}/refresh_content`);
  },
};
